import React, { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import List from '@mui/material/List'
import Drawer from '@mui/material/Drawer'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Badge from '@mui/material/Badge'
import { menuItems } from 'commons/utils/menu/menu'
import { APP_BAR_HEIGHT, MENU_WIDTH } from 'commons/constants/layout'
import useUser from 'commons/hooks/useUser'
import { EventType } from 'commons/utils/event'
import participationApi from 'services/participation'
import LogOut from 'components/LogOut'
import CustomScrollbar from 'components/CustomScrollbar'
import { MenuContent, StyledListItem } from './styles'
import { isActive } from './util'

type Props = {
  open: boolean
  onClose?: () => void
  variant?: 'permanent' | 'temporary' | 'persistent'
}

function Menu({ open = false, onClose, variant = 'permanent' }: Props) {
  const user = useUser()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = React.useState(false)
  const [pendingBadge, setPendingBadge] = React.useState<number>()

  React.useEffect(() => {
    let isMounted = true

    const fetchPending = () => {
      participationApi
        .pending()
        .then(res => {
          if (!isMounted) return
          setPendingBadge(res.pendingCount || 0)
        })
        .catch(() => {
          if (!isMounted) return
          setPendingBadge(undefined)
        })
    }

    fetchPending()

    const handleRefresh = () => fetchPending()
    window.addEventListener(EventType.REFRESH_PENDING_PARTICIPATIONS, handleRefresh)

    return () => {
      isMounted = false
      window.removeEventListener(EventType.REFRESH_PENDING_PARTICIPATIONS, handleRefresh)
    }
  }, [])

  const onItemClick = (path?: string) => {
    if (path) navigate(path)
  }

  const items = useMemo(
    () =>
      menuItems.filter(item => {
        const hasPermission = !item.permissions || user.hasPermission(item.permissions)
        const hasProfile = !item.profiles || user.hasProfile(...item.profiles)
        return hasPermission && hasProfile
      }),
    [user.hasPermission, user.hasProfile]
  )

  return (
    <Drawer
      anchor="left"
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        ['& .MuiDrawer-paper']: {
          width: hasScroll ? MENU_WIDTH + 8 : MENU_WIDTH,
          boxSizing: 'border-box',
          top: APP_BAR_HEIGHT,
          height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
          overflowX: 'hidden',
          overflowY: 'hidden',
        },
      }}
    >
      <MenuContent
        ref={scrollRef}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: hasScroll ? '10px' : '0px',
        }}
      >
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 4px',
          }}
        >
          {items.map((item, index) => (
            <Tooltip title={item.name} placement="right" key={index}>
              <StyledListItem
                selected={isActive(item.path)}
                onClick={() => {
                  item.setGtagMenu()
                  onItemClick(item.path)
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                    '& svg': { fontSize: 20 },
                  }}
                >
                  <Badge
                    badgeContent={item.path === '/participationConfirmation' ? pendingBadge : item.badge}
                    color="error"
                    sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 16, height: 16, padding: '0 3px' } }}
                  >
                    <item.icon />
                  </Badge>
                </ListItemIcon>

                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    variant: 'caption',
                    fontSize: 10,
                    textAlign: 'center',
                    noWrap: true,
                  }}
                  sx={{
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                />
              </StyledListItem>
            </Tooltip>
          ))}
        </List>
        <LogOut />
      </MenuContent>
      <CustomScrollbar
        containerRef={scrollRef}
        onVisibilityChange={setHasScroll}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />
    </Drawer>
  )
}

export default Menu
